(() => {
  const EXPERIMENT_ID = 'pdp_v1';
  const EXPERIMENT_PARAM = 'exp_pdp_v1';
  const EXPERIMENT_STORAGE_KEY = '__x4_exp_pdp_v1';
  const VISITOR_STORAGE_KEY = '__x4_vid';
  const TTL_DAYS = 30;
  const ALLOWED_HANDLES = new Set(['3x4-health', '3x4-blueprint']);
  const VARIANTS = ['control', 'variant'];

  function getProductHandleFromPath() {
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts.length < 2 || parts[0] !== 'products') return null;
    return decodeURIComponent(parts[1]).toLowerCase();
  }

  const productHandle = getProductHandleFromPath();
  if (!productHandle || !ALLOWED_HANDLES.has(productHandle)) return;

  function parseJson(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  function getCookie(name) {
    const pattern = new RegExp(`(?:^|; )${name}=([^;]*)`);
    const match = document.cookie.match(pattern);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function readStoredVariant() {
    let stored = null;
    try {
      stored = parseJson(localStorage.getItem(EXPERIMENT_STORAGE_KEY));
    } catch (error) {
      stored = null;
    }

    // Fall back to cookie if localStorage is unavailable or empty.
    if (!stored || !stored.variant) {
      try {
        stored = parseJson(getCookie(EXPERIMENT_STORAGE_KEY));
      } catch (error) {
        stored = null;
      }
    }

    if (!stored || !stored.variant || !stored.expires_at) return null;
    if (Date.now() > stored.expires_at) return null;
    return stored.variant;
  }

  function writeStoredVariant(variant) {
    const payload = {
      variant: variant,
      assigned_at: Date.now(),
      expires_at: Date.now() + TTL_DAYS * 24 * 60 * 60 * 1000
    };

    try {
      localStorage.setItem(EXPERIMENT_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      /* no-op */
    }

    // Also write to a first-party cookie so it is accessible from
    // Shopify's Web Pixel sandbox during checkout.
    setCookie(EXPERIMENT_STORAGE_KEY, JSON.stringify(payload), TTL_DAYS);
  }

  function getOverrideVariant() {
    const params = new URLSearchParams(window.location.search);
    const forcedVariant = (params.get(EXPERIMENT_PARAM) || '').toLowerCase();
    return VARIANTS.includes(forcedVariant) ? forcedVariant : null;
  }

  function generateVisitorId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `x4-${Date.now()}-${Math.floor(Math.random() * 1e8)}`;
  }

  function getVisitorId() {
    try {
      const localId = localStorage.getItem(VISITOR_STORAGE_KEY);
      if (localId) return localId;
    } catch (error) {
      /* no-op */
    }

    const cookieId = getCookie(VISITOR_STORAGE_KEY);
    if (cookieId) return cookieId;

    const newId = generateVisitorId();
    try {
      localStorage.setItem(VISITOR_STORAGE_KEY, newId);
    } catch (error) {
      /* no-op */
    }
    setCookie(VISITOR_STORAGE_KEY, newId, 365);
    return newId;
  }

  function hashString(input) {
    let hash = 0;
    for (let index = 0; index < input.length; index += 1) {
      hash = (hash << 5) - hash + input.charCodeAt(index);
      hash |= 0;
    }
    return Math.abs(hash);
  }

  function chooseVariant() {
    const visitorId = getVisitorId();
    const bucket = hashString(`${EXPERIMENT_ID}:${visitorId}`) % 2;
    return bucket === 0 ? 'control' : 'variant';
  }

  function applyVariantToDom(variant) {
    document.documentElement.dataset.expPdpV1 = variant;
    if (document.body) {
      document.body.classList.add(`exp-pdp-v1-${variant}`);
    }
  }

  function publishEvent(eventName, payload) {
    const basePayload = {
      experiment_id: EXPERIMENT_ID,
      variant_id: resolvedVariant,
      product_handle: productHandle,
      page_type: 'product',
      timestamp: new Date().toISOString()
    };
    const eventPayload = Object.assign(basePayload, payload || {});

    if (
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    ) {
      try {
        window.Shopify.analytics.publish(eventName, eventPayload);
      } catch (error) {
        /* no-op */
      }
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      Object.assign(
        {
          event: eventName
        },
        eventPayload
      )
    );
  }

  function publishAssignmentOnce(source) {
    const sessionKey = `__x4_exp_pdp_v1_assigned_${productHandle}`;
    let alreadyPublished = null;
    try {
      alreadyPublished = sessionStorage.getItem(sessionKey);
    } catch (error) {
      alreadyPublished = null;
    }
    if (alreadyPublished === resolvedVariant) return;

    try {
      sessionStorage.setItem(sessionKey, resolvedVariant);
    } catch (error) {
      /* no-op */
    }
    publishEvent('exp_variant_assigned', {
      assignment_source: source
    });
  }

  function applyVariantContent() {
    const titleNode = document.querySelector('.product-title');
    const headlineByHandle = {
      '3x4-health': 'Start Your Personalized DNA Health Plan',
      '3x4-blueprint': 'Start Your Personalized DNA Health Plan'
    };

    if (resolvedVariant === 'variant') {
      if (titleNode) {
        if (!titleNode.dataset.expOriginalHeadline) {
          titleNode.dataset.expOriginalHeadline = titleNode.textContent.trim();
        }
        titleNode.textContent = headlineByHandle[productHandle] || titleNode.dataset.expOriginalHeadline;

        let subheadlineNode = titleNode.parentNode.querySelector('.product-exp-subheadline');
        if (!subheadlineNode) {
          subheadlineNode = document.createElement('p');
          subheadlineNode.className = 'product-exp-subheadline';
          titleNode.insertAdjacentElement('afterend', subheadlineNode);
        }
        subheadlineNode.textContent = 'Actionable DNA insights in 2-3 weeks through the 3X4 Health App.';
      }

      document.querySelectorAll('[data-js-product-add-to-cart-text]').forEach((node) => {
        if (!node.dataset.expOriginalCta) {
          node.dataset.expOriginalCta = node.textContent.trim();
        }
        node.textContent = 'Get My DNA Kit';
      });
      return;
    }

    if (titleNode && titleNode.dataset.expOriginalHeadline) {
      titleNode.textContent = titleNode.dataset.expOriginalHeadline;
    }
    document.querySelectorAll('.product-exp-subheadline').forEach((node) => node.remove());
    document.querySelectorAll('[data-js-product-add-to-cart-text]').forEach((node) => {
      if (node.dataset.expOriginalCta) {
        node.textContent = node.dataset.expOriginalCta;
      }
    });
  }

  const forcedVariant = getOverrideVariant();
  let resolvedVariant = forcedVariant || readStoredVariant();
  let assignmentSource = 'persisted';

  if (!resolvedVariant) {
    resolvedVariant = chooseVariant();
    writeStoredVariant(resolvedVariant);
    assignmentSource = 'generated';
  } else if (forcedVariant) {
    assignmentSource = 'query_override';
  }

  if (!VARIANTS.includes(resolvedVariant)) {
    resolvedVariant = 'control';
  }

  applyVariantToDom(resolvedVariant);
  window.__x4Experiment = {
    experiment_id: EXPERIMENT_ID,
    variant_id: resolvedVariant,
    product_handle: productHandle
  };

  document.addEventListener('DOMContentLoaded', () => {
    applyVariantToDom(resolvedVariant);
    applyVariantContent();
    publishAssignmentOnce(assignmentSource);

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const cta = event.target.closest('[data-exp-primary-cta]');
      if (!cta) return;

      publishEvent('exp_cta_clicked', {
        cta_name: cta.getAttribute('name') || 'add_to_cart'
      });
    });
  });
})();
